import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineRedoIcon from 'LuxKit/Icons/line/LineRedoIcon'
import LineRoomUpgradeIcon from 'LuxKit/Icons/line/LineRoomUpgradeIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useOrder from 'hooks/Order/useOrder'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { dateIsAfterOrSame, dateIsBefore } from 'lib/datetime/dateUtils'
import LoyaltyPointsDeductionInfoModal from 'loyalty/components/LoyaltyPointsDeductionInfoModal'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { LUX_LOYALTY_POINTS_DEDUCTION_DATE } from '@luxuryescapes/lib-lux-loyalty'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useAppSelector } from 'hooks/reduxHooks'
import { LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE } from 'loyalty/constants/loyaltyConfig'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { isExpireWelcomePointsActivity } from 'api/mappers/loyalty/mapLoyaltyAccountActivity'

const MOBILE_ACTIVITY_ROW_COLUMNS = 'auto minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)'
const DESKTOP_ACTIVITY_ROW_COLUMNS = `auto minmax(0, 1fr) ${rem(80)} ${rem(60)} ${rem(92)} ${rem(56)}`
const NARROW_METRICS = '@media (max-width: 420px)'

const ActivityRow = styled.div`
  display: grid;
  grid-template:
    "icon details details details"
    "icon date date date"
    "icon metrics metrics metrics" auto / ${MOBILE_ACTIVITY_ROW_COLUMNS};
  row-gap: ${rem(4)};
  column-gap: ${rem(12)};
  padding: ${rem(8)} ${rem(8)} ${rem(12)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s;

  ${mediaQueryUp.tablet} {
    align-items: center;
    grid-template: "icon details date points status nights" auto / ${DESKTOP_ACTIVITY_ROW_COLUMNS};
    gap: ${rem(24)};
    padding: ${rem(8)} 0 ${rem(12)};
  }
`

const ActivityIcon = styled.div`
  grid-area: icon;
  height: ${rem(32)};
  width: ${rem(32)};
  ${setSvgIconSize(16)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`

const ActivityDetails = styled.div`
  grid-area: details;
  min-width: 0;
`

const ActivityDate = styled.div`
  grid-area: date;
  min-width: 0;
  margin-top: ${rem(6)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    text-align: center;
  }
`

const ActivityMetricsRow = styled.div`
  grid-area: metrics;
  display: flex;
  margin-top: ${rem(6)};

  ${NARROW_METRICS} {
    gap: ${rem(32)};
  }

  ${mediaQueryUp.tablet} {
    display: contents;
  }
`

const ActivityPoints = styled.div`
  grid-area: points;
  min-width: 0;
  flex: 0.75 1 0;

  ${NARROW_METRICS} {
    flex: 1 1 0;
  }

  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
  }
`

const ActivityPointsDashWrapper = styled.div`
  ${mediaQueryUp.tablet} {
    padding-left: ${rem(5)};
  }
`

const ActivityStatus = styled.div`
  grid-area: status;
  min-width: 0;
  flex: 1 1 0;

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(8)};
  }
`

const ActivityNights = styled.div`
  grid-area: nights;
  min-width: 0;
  flex: 1 1 0;
  margin-left: ${rem(16)};

  ${NARROW_METRICS} {
    margin-left: 0;
    padding-left: ${rem(48)};
  }

  ${mediaQueryUp.tablet} {
    margin-left: 0;
    padding-left: ${rem(8)};
  }
`

const Loading = styled(LoadingBox)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;

  height: ${rem(65)};

  ${mediaQueryUp.tablet} {
    height: ${rem(59)};
  }
`

interface Activity {
  icon?: React.ReactNode
  title: string
  subtitle?: string
  subtitleOnClick?: () => void
}

interface Props {
  activity: App.LoyaltyAccountActivity
}

function LoyaltyAccountLandingPageUserActivity(props: Props) {
  const intl = useIntl()
  const { activity } = props
  const [order, fetchingOrder] = useOrder(activity.orderId)
  const showModal = useContext(ModalContext)
  const hasLoyaltyPointsDeductionWarningEnabled = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltyPointsDeductionWarning])

  const { icon, title, subtitle, subtitleOnClick } = useMemo((): Activity => {
    const otherLabel = intl.formatMessage({
      id: '3tZ-Rqj6vlZ1y1_0EDhy',
      defaultMessage: 'Other',
      description: 'Fallback label for unrecognized loyalty activity type',
    })

    switch (activity.type) {
      case 'enrolment':
        return { title: intl.formatMessage({ id: '2t_bpjD8wShiLjmP1nZz', defaultMessage: 'Loyalty program enrolment', description: 'Activity title for loyalty program enrolment in the loyalty section' }) }
      case 'expired_points':
        return isExpireWelcomePointsActivity(activity.basePoints, activity.date)
          ? { title: intl.formatMessage({ id: 'NtYLq-9c7ZXAKsjem1V1', defaultMessage: 'Welcome points expired', description: 'Activity title when welcome points have expired on the promotional landing page' }) }
          : { title: intl.formatMessage({ id: 'UMis62hGzbP-c2agF6Tu', defaultMessage: 'Points expired', description: 'Activity title when loyalty points have expired on the promotional landing page' }) }
      case 'manual':
        return { title: intl.formatMessage({ id: 'fse3-0PirDHKglyLFdqr', defaultMessage: 'Manual adjustment', description: 'Activity title for manual points adjustment on the promotional landing page' }) }
      case 'tier_change':
        return { title: intl.formatMessage({ id: 'Nmajnl6SFRFOs5V2yBrS', defaultMessage: 'Tier change', description: 'Activity title for loyalty tier change in the loyalty program section' }), subtitle: activity.description }
      case 'expired_points_reversal':
        return { title: intl.formatMessage({ id: 'WO7d3Icy_ykrOnn6Y8EJ', defaultMessage: 'Points expired reversal', description: 'Activity title for expired points reversal on the promotional landing page' }) }
      case 'ad_hoc':
        return { title: activity.description ?? otherLabel }
      case 'other':
        return { title: activity.description ?? otherLabel }
      case 'points_ratio_adjust':
        return {
          title: intl.formatMessage({ id: 'S76XgjlJN63VC8SRNX8I', defaultMessage: 'Points adjustment', description: 'Activity title for points ratio adjustment on the promotional landing page' }),
          subtitle: intl.formatMessage({ id: 'lvybeuLd2R9RLNhBUvpu', defaultMessage: 'View details', description: 'Hyperlink text to view points adjustment details on the promotional landing page' }),
          subtitleOnClick: () => showModal(<LoyaltyPointsDeductionInfoModal />),
        }
      case 'debit_points':
      case 'cancellation':
      case 'benefit_redemption':
      case 'earn_points':
      case 'spend_points':
      case 'earn_status_credits':
      case 'credit_nights':
      case 'debit_nights':
      case 'pending_points': {
        const benefitCode = order?.loyaltyDetails.benefitsRedeemed.find(benefit => benefit.itemId === activity.orderItemId)?.code
        const hotelItem = order?.items.find(item => item.id === activity.orderItemId)
        if (hotelItem) {
          return {
            icon: benefitCode === 'hotel_upgrade' ? <LineRoomUpgradeIcon /> : <SolidBedIcon />,
            title: benefitCode === 'hotel_upgrade' ? intl.formatMessage({ id: '2TkMAcHHkoVAv4bTkGgG', defaultMessage: 'Room upgrade', description: 'Activity title for hotel room upgrade benefit on the promotional landing page' }) : hotelItem.offer.property?.name!,
            subtitle: hotelItem.reservation?.roomType?.name,
          }
        }

        const bedbankItem = order?.bedbankItems.find(item => item.id === activity.orderItemId)
        if (bedbankItem) {
          return {

            icon: <SolidBedIcon />,
            title: bedbankItem.offer.property.name,
            subtitle: bedbankItem.offer.packages[0].rate.room.name,
          }
        }

        const experienceItem = order?.experienceItems.find(item => item.id === activity.orderItemId)
        if (experienceItem) {
          let icon = <LineHotAirBalloonIcon />
          if (benefitCode === 'airport_transfer') {
            icon = <LineTaxiIcon />
          } else if (benefitCode === 'lounge_pass') {
            icon = <LineChairWithAirplaneIcon />
          }
          return {
            icon,
            title: activity.type === 'benefit_redemption' ? activity.description! : experienceItem.ticket.name,
            subtitle: activity.type === 'benefit_redemption' ? experienceItem.ticket.name : '',
          }
        }

        const cruiseItem = order?.cruiseItems.find(item => item.id === activity.orderItemId)
        if (cruiseItem) {
          return {
            icon: <LineShipIcon />,
            title: cruiseItem.offerName!,
          }
        }

        const insuranceItem = order?.insuranceItems.find(item => item.id === activity.orderItemId) || order?.bookingProtectionItems.find(item => item.id === activity.orderItemId)
        if (insuranceItem) {
          return { title: insuranceItem.productName }
        }

        const tourItem = order?.tourItems.find(item => item.id === activity.orderItemId)
        if (tourItem) {
          return {
            icon: <LineCompassIcon />,
            title: tourItem.tour.title,
          }
        }

        const tourExperienceItem = order?.tourExperienceItems.find(item => item.orderItemId === activity.orderItemId)
        if (tourExperienceItem) {
          return {
            icon: <LineCompassIcon />,
            title: tourExperienceItem.name,
          }
        }

        const flightItem = order?.flightItems.find(item => item.id === activity.orderItemId)
        if (flightItem) {
          return {

            icon: <LinePlaneDepartureIcon />,
            title: intl.formatMessage({ id: 'n26OfakSPx7MxwvtsIT3', defaultMessage: 'Flight', description: 'Activity title for flight booking in the flight search or booking flow' }),
            subtitle: flightItem.travellers?.map(traveller => `${traveller.givenName} ${traveller.surname}`).join(', '),
          }
        }

        const carHireItem = order?.carHireItems.find(item => item.id === activity.orderItemId)
        if (carHireItem) {
          return {
            icon: <LineCarIcon />,
            title: carHireItem.reservation?.vehicle.model ?? intl.formatMessage({ id: 'BCW92VYvcURgSZUCRRIL', defaultMessage: 'Car hire', description: 'Fallback activity title for car hire booking in the booking flow' }),
          }
        }

        const subscriptionItem = order?.subscriptionItems.find(item => item.id === activity.orderItemId)
        if (subscriptionItem) {
          if (activity.points < 0 || activity.statusCredits < 0) {
            return { title: intl.formatMessage({ id: 'op8DSLUZHz9N9vFIa6j5', defaultMessage: '{programName} renewal cancellation', description: 'Activity title for premium membership renewal cancellation' }, { programName: LUXURY_PLUS.PROGRAM_NAME }) }
          } else {
            return { title: intl.formatMessage({ id: 'qKE3vV04flTc4xGOuRNz', defaultMessage: '{programName} membership', description: 'Activity title for premium membership subscription' }, { programName: LUXURY_PLUS.PROGRAM_NAME }) }
          }
        }

        const customOfferItem = order?.customOfferItems.find(item => item.id === activity.orderItemId)
        if (customOfferItem) {
          // Determine icon based on the type of the first item in the custom offer
          const itemType = customOfferItem.custom_offer.items?.[0]?.type
          let icon = <LineInfoCircleIcon />

          switch (itemType) {
            case 'hotel':
              icon = <SolidBedIcon />
              break
            case 'flight':
              icon = <LinePlaneDepartureIcon />
              break
            case 'cruise':
              icon = <LineShipIcon />
              break
            case 'tour':
              icon = <LineCompassIcon />
              break
            case 'experience':
              icon = <LineHotAirBalloonIcon />
              break
            case 'other':
              icon = <LineInfoCircleIcon />
              break
          }

          return {
            icon,
            title: customOfferItem.custom_offer.name,
          }
        }

        // fallbacks if we didn't find any order items to describe the activity types
        switch (activity.type) {
          case 'debit_points':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'Zu2UwAXbW47iwKRWXAPy', defaultMessage: 'Points debited', description: 'Fallback subtitle for points debit activity on the promotional landing page' }) }
          case 'pending_points':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'uLqFEHpv2Tb0NGizBsd2', defaultMessage: 'Pending points', description: 'Fallback subtitle for pending points activity on the promotional landing page' }) }
          case 'benefit_redemption':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'zuEjBMw5Cnao4AsRAjEI', defaultMessage: 'Benefit redemption', description: 'Fallback subtitle for benefit redemption activity on the promotional landing page' }) }
          case 'earn_points':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'Z9MygakWNbIG7KErwN9z', defaultMessage: 'Points earned', description: 'Fallback subtitle for points earned activity on the promotional landing page' }) }
          case 'spend_points':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'b3V2meSGdsNvm4H4C8Jq', defaultMessage: 'Points spent', description: 'Fallback subtitle for points spent activity on the promotional landing page' }) }
          case 'earn_status_credits':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'DRcnG2hnlwKLW2We-3mN', defaultMessage: 'Status credits earned', description: 'Fallback subtitle for status credits earned activity' }) }
          case 'credit_nights':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: '-hhkiP5SQsXHWuJvYApD', defaultMessage: 'Nights credited', description: 'Fallback subtitle for nights credited activity on the promotional landing page' }) }
          case 'debit_nights':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: '892Rn2fqqYcfrvKzV9hw', defaultMessage: 'Nights debited', description: 'Fallback subtitle for nights debited activity on the promotional landing page' }) }
          case 'cancellation':
            return { title: activity.description!, subtitle: intl.formatMessage({ id: 'sIWK54DJfFLGJKhsNFDC', defaultMessage: 'Cancellation', description: 'Fallback subtitle for cancellation activity in the cancellation flow' }) }
        }
      }
    }
  }, [activity, order, showModal, intl])

  const handleSubtitleOnClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()
    subtitleOnClick?.()
  }, [subtitleOnClick])

  const isPending = activity.type === 'pending_points'
  const getMetricIcon = useCallback((value?: number) => {
    if (!value) return undefined
    if (isPending) {
      return <LineRedoIcon colour="warning" size={12} />
    } else if (value > 0) {
      return <LinePlusIcon colour="success" size={12} />
    } else if (value < 0) {
      return <LineMinusIcon colour="critical" size={12} />
    }
  }, [isPending])
  const pointsIcon = useMemo(() => getMetricIcon(activity.points), [activity.points, getMetricIcon])
  const statusCreditsIcon = useMemo(() => getMetricIcon(activity.statusCredits), [activity.statusCredits, getMetricIcon])

  const hasPendingPointsDeduction = isPending && dateIsBefore(new Date(activity.date), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE))

  const hasAccrualPointsDeduction =
    activity.type === 'debit_points' &&
    order &&
    // Booking date is before the points deduction date
    dateIsBefore(new Date(order.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE)) &&
    // Accrual happened after 27th June 2025 AEST
    dateIsAfterOrSame(new Date(activity.date), new Date(LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE))

  const hasPointsDeduction =
    (hasPendingPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled) ||
    (hasAccrualPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled)

  const handleShowPointsDeductionModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()
    showModal(<LoyaltyPointsDeductionInfoModal />)
  }, [showModal])

  const isMobile = !useScreenSizeUp('tablet')
  const activityValueVariant = isMobile ? 'small' : 'medium'
  const displayedNights = isPending ? activity.pendingNights : activity.numNights
  const nightsIcon = useMemo(() => getMetricIcon(displayedNights), [displayedNights, getMetricIcon])

  const pointsAdjustedLabel = intl.formatMessage({
    id: 'E8ANQrcHlAovwu1rNOAr',
    defaultMessage: 'Points adjusted',
    description: 'Link text indicating points were adjusted due to deduction',
  })

  const pointsHeaderLabel = intl.formatMessage({
    id: 'hzbnP4_LPAHL1ObHXTeD',
    defaultMessage: 'Points',
    description: 'Table column header for points in loyalty activity',
  })

  const statusCreditsHeaderLabel = intl.formatMessage({
    id: 'jIKI2okZHux7Nhf2FyHC',
    defaultMessage: 'Status Credits',
    description: 'Column label for status credits in mobile loyalty activity',
  })

  const nightsHeaderLabel = intl.formatMessage({
    id: '5Ep2sq69xGBPGLtbFV7_',
    defaultMessage: 'Nights',
    description: 'Table column header for nights in loyalty activity',
  })

  if (fetchingOrder) {
    return <ActivityRow><Loading /></ActivityRow>
  }

  return <ActivityRow>
    <ActivityIcon>{icon || <LineInfoCircleIcon />}</ActivityIcon>
    <ActivityDetails>
      <Group direction="vertical" gap={2}>
        <BodyText variant="medium" colour="neutral-two">
          {title}
        </BodyText>
        {!!subtitle && <Group direction="horizontal" gap={8}>
          {!subtitleOnClick && <BodyText variant="small" colour="neutral-two">
            {subtitle}
          </BodyText>}
          {!!subtitleOnClick && <TextLink size="small" weight="regular" onClick={handleSubtitleOnClick}>
            {subtitle}
          </TextLink>}
          {hasPointsDeduction && <TextLink size="small" weight="regular" onClick={handleShowPointsDeductionModal}>{pointsAdjustedLabel}</TextLink>}
        </Group>}
      </Group>
    </ActivityDetails>
    <ActivityDate>
      <BodyText variant={activityValueVariant} colour="neutral-three" wrap="truncate">
        <FormatDateTime value={activity.date} format="dmy-short" />
      </BodyText>
    </ActivityDate>
    <ActivityMetricsRow>
      <ActivityPoints>
        <CSSBreakpoint max="mobile">
          <BodyText variant="small" colour="neutral-two" weight="bold">{pointsHeaderLabel}</BodyText>
        </CSSBreakpoint>
        {!!activity.points && <BodyTextBlock
          variant={activityValueVariant}
          startIcon={pointsIcon}
          colour={isPending ? 'neutral-three' : 'neutral-two'}
          align="start"
        >
          <FormatDecimal value={Math.abs(activity.points)} />
        </BodyTextBlock>}
        {!activity.points && <ActivityPointsDashWrapper>
          <BodyTextBlock
            variant={activityValueVariant}
            colour={isPending ? 'neutral-three' : 'neutral-two'}
            align="start"
            wrap={isMobile ? 'truncate' : undefined}
          >
            <NotAvailablePlaceholder variant="single" />
          </BodyTextBlock>
        </ActivityPointsDashWrapper>}
      </ActivityPoints>
      <ActivityStatus>
        <CSSBreakpoint max="mobile">
          <BodyText variant="small" colour="neutral-two" weight="bold" wrap="no-wrap">{statusCreditsHeaderLabel}</BodyText>
        </CSSBreakpoint>
        <BodyTextBlock
          variant={activityValueVariant}
          startIcon={statusCreditsIcon}
          colour={isPending ? 'neutral-three' : 'neutral-two'}
          align="start"
          wrap={isMobile ? 'truncate' : undefined}
        >
          {activity.statusCredits ? <FormatDecimal value={Math.abs(activity.statusCredits)} /> : <NotAvailablePlaceholder variant="single" />}
        </BodyTextBlock>
      </ActivityStatus>
      <ActivityNights>
        <CSSBreakpoint max="mobile">
          <BodyText variant="small" colour="neutral-two" weight="bold">{nightsHeaderLabel}</BodyText>
        </CSSBreakpoint>
        <BodyTextBlock
          variant={activityValueVariant}
          startIcon={nightsIcon}
          colour={isPending ? 'neutral-three' : 'neutral-two'}
          align="start"
          wrap={isMobile ? 'truncate' : undefined}
        >
          {displayedNights ? <FormatDecimal value={Math.abs(displayedNights)} /> : <NotAvailablePlaceholder variant="single" />}
        </BodyTextBlock>
      </ActivityNights>
    </ActivityMetricsRow>
  </ActivityRow>
}

export default LoyaltyAccountLandingPageUserActivity
